import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaUser,
  FaShoppingCart,
  FaHeart,
  FaRegHeart,
  FaRegUser,
  FaShoppingBag,
  FaTimes,
} from "react-icons/fa";
import { useCart } from "./Cart/CartContext";
import { useNavbar } from "../contexts/NavbarContext";
import logoImage from "../assets/logo2.png";

export default function Navbar() {
  const cart = useCart();
  const totalQuantity = cart?.totalQuantity || 0;
  const location = useLocation();
  const { showNavbar, setShowNavbar, hideNavbar } = useNavbar();

  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showSubMenu, setShowSubMenu] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isMobileScrolled, setIsMobileScrolled] = useState(false);
  const [forceHidden, setForceHidden] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const isMobile = window.innerWidth <= 768;

      // Si la navbar est forcée d'être cachée par le bouton de fermeture, on la cache
      if (forceHidden) {
        setIsVisible(false);
        return;
      }

      // Si la navbar est forcée d'être visible par le bouton, on ne la cache pas
      if (showNavbar) {
        setIsVisible(true);
        setForceHidden(false); // Réinitialiser le cache forcé
        return;
      }

      // Sur mobile, la navbar se comporte comme sur desktop
      if (isMobile) {
        if (scrollTop === 0) {
          // Au top - hide navbar
          setIsVisible(false);
          setIsMobileScrolled(false);
        } else if (scrollTop > lastScrollY && scrollTop > 100) {
          // Scrolling down - hide navbar
          setIsVisible(false);
          setIsMobileScrolled(true);
        } else if (scrollTop < lastScrollY) {
          // Scrolling up - show navbar
          setIsVisible(true);
          setIsMobileScrolled(true);
        }
        setLastScrollY(scrollTop);
        return;
      }

      // Sur desktop, la navbar disparaît lors du scroll
      if (scrollTop === 0) {
        // Au top - hide navbar
        setIsVisible(false);
      } else if (scrollTop > lastScrollY && scrollTop > 100) {
        // Scrolling down - hide navbar
        setIsVisible(false);
      } else if (scrollTop < lastScrollY) {
        // Scrolling up - show navbar
        setIsVisible(true);
      }

      setLastScrollY(scrollTop);
    };

    // Appeler handleScroll immédiatement pour définir l'état initial
    handleScroll();

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [lastScrollY, location.pathname, showNavbar]);

  // Réinitialiser l'état forcé de la navbar après un délai
  useEffect(() => {
    if (showNavbar) {
      const timer = setTimeout(() => {
        hideNavbar();
      }, 5000); // La navbar reste visible pendant 5 secondes après le clic

      return () => clearTimeout(timer);
    }
  }, [showNavbar, hideNavbar]);

  // Réinitialiser l'état forcé de cache après un délai
  useEffect(() => {
    if (forceHidden) {
      const timer = setTimeout(() => {
        setForceHidden(false);
      }, 3000); // La navbar reste cachée pendant 3 secondes après le clic sur fermer

      return () => clearTimeout(timer);
    }
  }, [forceHidden]);

  const toggleMobileMenu = () => {
    setShowMobileMenu(!showMobileMenu);
    if (showSubMenu) setShowSubMenu(false);
  };

  const toggleSubMenu = () => {
    setShowSubMenu(!showSubMenu);
  };

  const handleLinkClick = () => {
    setShowMobileMenu(false);
    setShowSubMenu(false);
  };

  const handleHideNavbar = () => {
    hideNavbar(); // Cache showNavbar
    setForceHidden(true); // Force le cache même pendant le scroll
  };

  return (
    <>
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        header {
          width: 100%;
          font-family: Georgia, Utopia, "Times New Roman", Times, serif;
          position: fixed;
          top: 0;
          z-index: 1000;
          transform: translateY(0);
          transition: transform 0.4s ease-in-out, opacity 0.3s ease-in-out;
        }

        header.navbar-hidden {
          transform: translateY(-100%);
          opacity: 0;
        }
        
        header.navbar-visible {
          transform: translateY(0);
          opacity: 1;
        }

        .navbar-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px 40px;
         
          background-color: #FFF; 
          color: #4B3B1A;
          position: relative;
          border-bottom: 1px solid #eee;
          transition: all 0.3s ease;
        }

        .navbar-top.mobile-scrolled {
          background: #FAF6E7;
          border-bottom: 1px solid #eee;
        }

        .logo-container {
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          align-items: center;
          gap: 30px;
          user-select: none;
        }

        .logo img {
          height: 50px;
          object-fit: contain;
        }

        .brand-name {
          font-size: 32px;
          font-weight: 800;
          color: #4B3B1A;
          letter-spacing: 2px;
          font-family: 'Playfair Display', serif;
          text-transform: uppercase;
          user-select: none;
          transition: color 0.3s ease;
        }

      

        .logo-icons {
          display: none;
          align-items: center;
          gap: 20px;
          margin-left: 20px;
        }

        .logo-icons .icon {
          font-size: 16px;
          color: #4B3B1A;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          display: inline-block;
        }

        .logo-icons .icon:hover {
          color: #d4af37;
          transform: translateY(-2px) scale(1.1);
          filter: drop-shadow(0 4px 8px rgba(212, 175, 55, 0.3));
        }

      

        .navbar-top.mobile-scrolled .logo-icons .icon:hover {
          color: #d4af37;
        }

        .navbar-icons {
          display: flex;
          align-items: center;
          gap: 20px;
          margin-left: auto;
          position: relative;
          z-index: 2;
          padding: 8px 16px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 25px;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(212, 175, 55, 0.1);
          transition: all 0.3s ease;
        }

        .navbar-icons:hover {
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(212, 175, 55, 0.2);
          box-shadow: 0 4px 20px rgba(212, 175, 55, 0.1);
        }


        .icon {
          font-size: 18px;
          color: #4B3B1A;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          display: inline-block;
        }

        .icon:hover {
          color: #d4af37;
          transform: translateY(-2px) scale(1.1);
          filter: drop-shadow(0 4px 8px rgba(212, 175, 55, 0.3));
        }

        .navbar-icons button.icon {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .navbar-icons button.icon:hover {
          color: #d4af37;
          transform: translateY(-2px) scale(1.1);
          filter: drop-shadow(0 4px 8px rgba(212, 175, 55, 0.3));
        }

        

        .navbar-top.mobile-scrolled .icon:hover {
          color: #d4af37;
        }

        .icon::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 0;
          height: 0;
          background: rgba(212, 175, 55, 0.1);
          border-radius: 50%;
          transform: translate(-50%, -50%);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: -1;
        }

        .icon:hover::before {
          width: 40px;
          height: 40px;
        }

        .cart-icon {
          position: relative;
          text-decoration: none;
        }

        .cart-count {
          position: absolute;
          top: -8px;
          right: -12px;
          background: linear-gradient(135deg, #d4af37, #b8860b);
          color: white;
          font-size: 11px;
          border-radius: 50%;
          padding: 2px 6px;
          min-width: 18px;
          height: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          box-shadow: 0 2px 8px rgba(212, 175, 55, 0.4);
          animation: pulse 2s infinite;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        @keyframes pulse {
          0% {
            transform: scale(1);
            box-shadow: 0 2px 8px rgba(212, 175, 55, 0.4);
          }
          50% {
            transform: scale(1.1);
            box-shadow: 0 4px 12px rgba(212, 175, 55, 0.6);
          }
          100% {
            transform: scale(1);
            box-shadow: 0 2px 8px rgba(212, 175, 55, 0.4);
          }
        }

        nav.navbar-bottom {
          display: flex;
          justify-content: left;
          gap: 30px;
          padding: 10px 0 10px 35px;
          border-top: 1px solid #eee;
          background-color: #FAF6E7;
          flex-wrap: wrap;
          flex-shrink: 0;
          user-select: none;
          display: flex
;
    justify-content: center;
        }

        .nav-link {
          color: #4B3B1A;
          text-decoration: none;
          font-weight: 500;
          font-size: 14px;
          text-transform: uppercase;
          letter-spacing: 1px;
          position: relative;
          padding: 5px 0;
          transition: color 0.3s ease;
          display: inline-block;
          cursor: pointer;
        }

        .nav-link:hover {
          color: #362E11;
        }

      
        .navbar-top.mobile-scrolled ~ nav .nav-link:hover {
          color: #362E11;
        }

        .nav-link::after {
          content: "";
          position: absolute;
          bottom: 0;
          left: 0;
          width: 0;
          height: 1px;
          background-color: #4B3B1A;
          transition: width 0.3s ease;
        }

        .nav-link:hover::after {
          width: 100%;
        }

        .navbar-top.mobile-scrolled ~ nav .nav-link::after {
          background-color: #4B3B1A;
        }

        .dropdown-container {
          position: relative;
        }

        .dropdown-container:hover .dropdown-menu {
          display: flex;
        }

        .dropdown-menu {
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          background: #FAF6E7;
          box-shadow: 0 5px 15px rgba(0,0,0,0.1);
          padding: 10px 0;
          z-index: 999;
          display: none;
          flex-direction: column;
          min-width: 200px;
          border-radius: 4px;
        }

        .dropdown-menu a {
          padding: 12px 25px;
          color: #4B3B1A;
          text-decoration: none;
          font-size: 13px;
          text-transform: uppercase;
          transition: background-color 0.2s ease;
        }

        .dropdown-menu a:hover {
          background-color: #f8f8f8;
          color: #362E11;
        }

        .mobile-nav-toggle {
          display: none;
          font-size: 26px;
          cursor: pointer;
          user-select: none;
          color: #4B3B1A;
          background: none;
          border: none;
          margin-left: 20px;
          z-index: 2;
        }


        .mobile-nav-menu {
          display: none;
          flex-direction: column;
          background: linear-gradient(135deg, #2c1810 0%, #5a4a3a 50%, #2c1810 100%);
          backdrop-filter: blur(20px);
          border-top: 1px solid rgba(212, 175, 55, 0.3);
          padding: 10px 20px 20px;
          gap: 10px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
          position: relative;
          overflow: hidden;
        }

        .mobile-nav-menu::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="rgba(212, 175, 55, 0.1)"/><circle cx="75" cy="75" r="1" fill="rgba(212, 175, 55, 0.1)"/><circle cx="50" cy="10" r="0.5" fill="rgba(212, 175, 55, 0.05)"/><circle cx="10" cy="60" r="0.5" fill="rgba(212, 175, 55, 0.05)"/><circle cx="90" cy="40" r="0.5" fill="rgba(212, 175, 55, 0.05)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
          opacity: 0.3;
          pointer-events: none;
        }

        .mobile-submenu {
          display: flex;
          flex-direction: column;
          margin-left: 15px;
          gap: 8px;
          margin-top: 8px;
          border-left: 2px solid #4B3B1A;
          padding-left: 15px;
          padding-bottom: 10px;
        }

        .mobile-submenu a {
          color: rgba(255, 255, 255, 0.9);
          text-decoration: none;
          font-size: 14px;
          text-transform: uppercase;
          font-weight: 500;
          letter-spacing: 0.5px;
          padding: 8px 0;
          transition: all 0.3s ease;
          display: block;
          position: relative;
          z-index: 1;
        }

        .mobile-submenu a:hover {
          color: #d4af37;
          padding-left: 15px;
          background: rgba(212, 175, 55, 0.1);
          border-radius: 6px;
          margin: 0 -5px;
          padding-left: 20px;
        }

        .mobile-icons {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 25px;
          margin-top: 10px;
        }

        .mobile-nav-menu .icon {
          color: white;
          transition: all 0.3s ease;
        }

        .mobile-nav-menu .icon:hover {
          color: #d4af37;
          transform: scale(1.1);
        }

        .mobile-icons button.icon {
          transition: all 0.3s ease;
        }

        .mobile-icons button.icon:hover {
          color: #d4af37;
          transform: scale(1.1);
        }

                @media (max-width: 768px) {
  .navbar-top {
    padding: 15px 20px;
  }

  nav.navbar-bottom {
    display: none;
  }

  .mobile-nav-toggle {
    display: block;
  }

  .navbar-icons {
    display: none;
  }

  .logo-icons {
    display: flex;
  }

  .mobile-nav-menu {
    display: flex;
    
  }

  .mobile-nav-menu .nav-link {
    font-size: 16px;
    padding: 10px 0;
    border-bottom: 1px solid rgba(212, 175, 55, 0.2);
    color: white;
    position: relative;
    z-index: 1;
    transition: all 0.3s ease;
  }

  .mobile-nav-menu .nav-link:hover {
    color: #d4af37;
    padding-left: 10px;
    background: rgba(212, 175, 55, 0.1);
    border-radius: 8px;
    margin: 0 -10px;
    padding-left: 20px;
  }
}

      `}</style>

      <header className={(isVisible || showNavbar) ? "navbar-visible" : "navbar-hidden"}>
        {/* Barre haute */}
        <div
          className={`navbar-top ${isMobileScrolled ? "mobile-scrolled" : ""}`}
        >
          <div className="logo-container">
            <Link to="/" className="logo" onClick={handleLinkClick}>
              <img src={logoImage} alt="Logo SAFA" />
            </Link>
            <span className="brand-name">SAFA</span>

            {/* Icônes à côté du logo */}
            <div className="logo-icons">
              <Link to="/cart" className="cart-icon" onClick={handleLinkClick}>
                <FaShoppingBag className="icon" />
                {totalQuantity > 0 && (
                  <span className="cart-count">{totalQuantity}</span>
                )}
              </Link>
              <Link to="/profile" onClick={handleLinkClick}>
                <FaRegUser className="icon" />
              </Link>
            </div>
          </div>

          <div className="navbar-icons">
            <Link to="/favoris" onClick={handleLinkClick}>
              <FaRegHeart className="icon" />
            </Link>
            <Link to="/profile" onClick={handleLinkClick}>
              <FaRegUser className="icon" />
            </Link>
            <Link to="/cart" className="cart-icon" onClick={handleLinkClick}>
              <FaShoppingBag className="icon" />
              {totalQuantity > 0 && (
                <span className="cart-count">{totalQuantity}</span>
              )}
            </Link>
            
            {/* Bouton pour cacher la navbar - visible seulement si showNavbar est true */}
            {showNavbar && (
              <motion.button
                onClick={handleHideNavbar}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="icon"
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "0",
                  margin: "0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                title="Masquer le menu"
              >
                <FaTimes />
              </motion.button>
            )}
          </div>

          {/* Bouton hamburger */}
          <button
            className="mobile-nav-toggle"
            onClick={toggleMobileMenu}
            aria-label="Ouvrir menu mobile"
          >
            ☰
          </button>
        </div>

        {/* Desktop */}
        <nav className="navbar-bottom">
          <Link to="/" className="nav-link">
            ACCUEIL
          </Link>

          <div className="dropdown-container">
            <div className="nav-link">NOS BABOUCHES ▾</div>
            <div className="dropdown-menu">
              <Link to="/products/femme" onClick={handleLinkClick}>
                FEMME
              </Link>
              <Link to="/products/homme" onClick={handleLinkClick}>
                HOMME
              </Link>
              <Link to="/products/enfant" onClick={handleLinkClick}>
                ENFANT
              </Link>
              <Link
                to="/products/collections-speciales"
                onClick={handleLinkClick}
              >
                COLLECTIONS SPÉCIALES
              </Link>
            </div>
          </div>

          <Link to="/about" className="nav-link">
            À PROPOS DE SAFA
          </Link>
          <Link to="/fabrication" className="nav-link">
            FABRICATION ARTISANALE
          </Link>
          <Link to="/faq" className="nav-link">
            FAQ
          </Link>
          <Link to="/guide-tailles" className="nav-link">
            GUIDE DE TAILLE
          </Link>
          <Link to="/conseils" className="nav-link">
            BLOG / ENTRETIEN
          </Link>
          <Link to="/contact" className="nav-link">
            CONTACT
          </Link>
        </nav>

        {/* Mobile */}
        {showMobileMenu && (
          <nav className="mobile-nav-menu">
            <Link to="/" className="nav-link" onClick={handleLinkClick}>
              ACCUEIL
            </Link>
            <div
              className="nav-link"
              onClick={toggleSubMenu}
              aria-expanded={showSubMenu}
              aria-controls="mobile-submenu"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggleSubMenu();
                }
              }}
            >
              NOS BABOUCHES ▾
            </div>
            {showSubMenu && (
              <div className="mobile-submenu" id="mobile-submenu">
                <Link to="/products/femme" onClick={handleLinkClick}>
                  FEMME
                </Link>
                <Link to="/products/homme" onClick={handleLinkClick}>
                  HOMME
                </Link>
                <Link to="/products/enfant" onClick={handleLinkClick}>
                  ENFANT
                </Link>
                <Link
                  to="/products/collections-speciales"
                  onClick={handleLinkClick}
                >
                  COLLECTIONS SPÉCIALES
                </Link>
              </div>
            )}

            <Link to="/about" className="nav-link" onClick={handleLinkClick}>
              À PROPOS DE SAFA
            </Link>
            <Link
              to="/fabrication"
              className="nav-link"
              onClick={handleLinkClick}
            >
              FABRICATION ARTISANALE
            </Link>
            <Link to="/faq" className="nav-link" onClick={handleLinkClick}>
              FAQ
            </Link>
            <Link
              to="/guide-tailles"
              className="nav-link"
              onClick={handleLinkClick}
            >
              GUIDE DE TAILLE
            </Link>
            <Link to="/conseils" className="nav-link" onClick={handleLinkClick}>
              BLOG / ENTRETIEN
            </Link>
            <Link to="/contact" className="nav-link" onClick={handleLinkClick}>
              CONTACT
            </Link>

            {/* Icons version mobile  */}
            <div className="mobile-icons">
              <Link to="/favoris" onClick={handleLinkClick}>
                <FaRegHeart className="icon" />
              </Link>
              <Link to="/profile" onClick={handleLinkClick}>
                <FaRegUser className="icon" />
              </Link>
              <Link to="/cart" className="cart-icon" onClick={handleLinkClick}>
                <FaShoppingBag className="icon" />
                {totalQuantity > 0 && (
                  <span className="cart-count">{totalQuantity}</span>
                )}
              </Link>
              
              {/* Bouton pour cacher la navbar (version mobile) - visible seulement si showNavbar est true */}
              {showNavbar && (
                <motion.button
                  onClick={handleHideNavbar}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="icon"
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "0",
                    margin: "0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  title="Masquer le menu"
                >
                  <FaTimes />
                </motion.button>
              )}
            </div>
          </nav>
        )}
      </header>
    </>
  );
}
